import { LuminanceStatistics } from "./ai-calibration-brightness"
import { EstimatedIlluminant } from "./ai-calibration-wb"
import { clamp, l2Distance } from "@/utils/math"
import { CullingInfoRecord, ImageInfoRecord, PhotoMetadataRecord } from "@/lib/catalog"
import { Rect } from "@/lib/ai/detection/face-detection"
import {
	absoluteDifference,
	extractKeys,
	isCropEqual,
	isEmpty,
	meanAbsoluteDifference,
	normalizeBySum,
	percentDifference,
	percentRatioToMin,
} from "./ai-calibration-utils"
import {
	DefaultExcludedAdjustmentKeys,
	getAdjustmentRange,
	getDefaultAdjustments,
	getKeysOfDefaultAdjustments,
} from "@/core/adjustments"
import { AdjustmentKey, Adjustments, Crop, MaskGroup } from "@/core/types"
import { SceneDetectionResult } from "@/lib/ai/detection/scene-detection"
import { computeWeightedLocalBiases } from "./ai-calibration-mask"
import { logAiInfo } from "@/lib/ai/utils"
import { FileFormat } from "@/utils/file"
import equal from "fast-deep-equal"
import { InputColorMatchFeatures, ReferenceColorMatchFeatures } from "../color-match/utils"
import { getDecreasedValue } from "../color-match/utils"
import { colorDifference } from "../color-match/point-color"
import { vec3 } from "gl-matrix"
import { atom } from "jotai"
import { getDefaultStore } from "jotai"

// AI Style Configuration Atoms
export const numRefsForColorMatchAtom = atom(3) // Default to 3 references
export const useLRParamsForJpegReferenceAtom = atom(true) // Default to using LR params

// Getter functions that read from atoms
export const getNumRefsForColorMatch = (): number => {
	return getDefaultStore().get(numRefsForColorMatchAtom)
}

export const getUseLRParamsForJpegReference = (): boolean => {
	return getDefaultStore().get(useLRParamsForJpegReferenceAtom)
}

export const getDefaultKNNRefs = (): number => {
	return getNumRefsForColorMatch() + 1 // number of references to use for AI style (including virtual default reference)
}

// Legacy constants - use getter functions instead
// export const NumRefsForColorMatch: number = 3 // number of references to use for color match computation in AI style
// export const ShareReferenceForSimilarKeyPhotos = NumRefsForColorMatch === 1 // set to true if NumRefsForColorMatch==1 for more consistent color
// const DefaultKNNRefs = NumRefsForColorMatch + 1 // number of references to use for AI style (including virtual default reference)
// export const UseLRParamsForJpegReference = true // Flag to use LR params embedded in the jpegs

export const allAdjustmentKeys = getKeysOfDefaultAdjustments() as AdjustmentKey[]

export const lightKeys = [
	"exposure",
	"highlights",
	"shadows",
	"contrast",
	"blacks",
	"whites",
] as const
export type LightKey = (typeof lightKeys)[number]

export const wbKeys = ["temperature", "tint"] as const
export type WBKey = (typeof wbKeys)[number]

export interface BiasType extends Adjustments {
	localAdjustmentsManual?: MaskGroup[]
}
type BiasKey = keyof BiasType
const allBiasKeys: BiasKey[] = [...allAdjustmentKeys, "localAdjustmentsManual"]

export const defaultReferenceId = "default-zero-bias-reference-photo-id"
const defaultDistances: FeatureDistances = [0.35, 0.3, 0.25, 0.5, 0.35, 0.32, 0.08] // scene, light, wb, meta, overall, clusterDist, overallWbDist
const defaultOverallDist = 0.35
const overallDistanceWeight = [0.5, 0.1, 0.2, 0.2] //[0.5, 0.13, 0.17, 0.2],  old: [0.45, 0.2, 0.15, 0.2]

export const saturationBiasBW = -100

export interface Metadata {
	iso: number | null
	timestamp: number | null
	flash: boolean
	camera: string | null
	init_temp: number
	init_tint: number
	format: FileFormat
}

export interface SceneWhiteBalance {
	asShotTemperature: number
	asShotTint: number
}

export interface DeepClusterResult {
	deepClusterFeature: Float32Array
}

export interface SceneInfo {
	imageSize: [number, number] // [w, h]: oriented image size with correct ratio
	initialOrientation?: number
	faceBoxCoords: Rect[]
	deepCluster?: DeepClusterResult // deprecated
	scene?: SceneDetectionResult
	light: LuminanceStatistics
	autoLightParams?: Partial<Adjustments>
	autoWbParams?: Partial<Adjustments>
	wb: EstimatedIlluminant
	autoCorrection: Partial<Adjustments> // store auto straighten and possible future feature like auto-perspective-correction params etc.
	colorMatch?: InputColorMatchFeatures
}

export interface SceneBinaryData {
	// bigger binary data, store in imageInfo as separate fields as blob in db
	sceneClusterFeature?: Float32Array | null
	faceColors?: Float32Array | null
	faceWeights?: Float32Array | null
}

export interface ImageFeature extends SceneInfo {
	meta: Metadata
	verySimilarNeighbor?: string | null // id of the very similar neighbor that's different from key photo
	useOwnAutoLightMap?: Record<string, boolean> | null // key: id of the keyPhoto; value: true - use own auto light params, false - only adapt exposure from key photo's params
}

// [scene, light, wb, meta, overall, clusterDist, overallWbDist]; note clusterDist, overallWbDist are added later, so check for backward compatibility
export type FeatureDistances = [number, number, number, number, number, number, number]

export interface DistanceMap {
	[imageId: string]: FeatureDistances
}

export interface PartialAdjustmentsMap {
	[presetId: string]: Partial<Adjustments>
}

export const refCurrentAdjustmentsId =
	"special-preset-id-for-storing-current-adjustments-of-reference"

export const createImageInfo = (
	imageId: string,
	features: ImageFeature,
	sceneBinaryData: SceneBinaryData,
): ImageInfoRecord => {
	return {
		imageId: imageId,
		features: features,
		presetId: null,
		aiAdjustmentsMap: {},
		presetAdjustmentsMap: null,
		aiStyleAdjustments: null,
		biases: null,
		nearestReferences: null,
		keyNeighborId: null,
		needApplyStyle: null,
		sceneClusterFeature: sceneBinaryData.sceneClusterFeature ?? null,
		faceColors: sceneBinaryData.faceColors ?? null,
		faceWeights: sceneBinaryData.faceWeights ?? null,
	}
}

export function isImageInfoManuallyUpdated(a: ImageInfoRecord, b: ImageInfoRecord) {
	if ((!a && b) || (a && !b)) {
		return true
	}

	return (
		a.imageId != b.imageId ||
		a.presetId != b.presetId ||
		!isEqual(a.biases ?? {}, b.biases ?? {}) ||
		!isReferenceColorMatchEqual(a.colorMatchReference, b.colorMatchReference)
	)
}

export function isReferenceColorMatchEqual(
	a: ReferenceColorMatchFeatures | null | undefined,
	b: ReferenceColorMatchFeatures | null | undefined,
) {
	if (!a && !b) {
		return true
	}
	if (!a || !b) {
		return false
	}
	return equal(a, b)
}

function timeDifference(t1, t2, defaultDiff = 1) {
	if (t1 == null || t2 == null) {
		return defaultDiff
	}
	const minuteDiff = Math.abs(t1 - t2) / 60000
	return Math.min(Math.max(minuteDiff - 5, 0), 35) / 35
}

function timeDifferenceNew(t1, t2, defaultDiff = 1) {
	if (t1 == null || t2 == null) {
		return defaultDiff
	}
	const minuteDiff = Math.abs(t1 - t2) / 60000
	return Math.min(Math.max(minuteDiff - 2, 0), 88) / 88
}

function cameraDifference(a, b, defaultDiff = 0.5) {
	if (a == null || b == null) {
		return defaultDiff
	}
	if (a === b) return 0
	else return 1
}

function formatDifference(a: FileFormat | undefined, b: FileFormat | undefined, defaultDiff = 0.2) {
	if (a == undefined || b == undefined) {
		return defaultDiff
	}
	if (a === "RAW" && b === "RAW") return 0
	else if (a !== "RAW" && b !== "RAW") return 0
	else return 1
}

export function imageRatioDifference(f1: ImageFeature, f2: ImageFeature) {
	const r1 = f1.imageSize[0] / f1.imageSize[1]
	const r2 = f2.imageSize[0] / f2.imageSize[1]
	const minDiff = (r1 >= 1 && r2 < 1) || (r1 < 1 && r2 >= 1) ? 0.01 : 0
	return Math.min(Math.abs(r1 - r2) / 100, 0.04) + minDiff
}

export function getMetaDistance(
	f1: ImageFeature,
	f2: ImageFeature,
	includesTimestamp: boolean = true,
) {
	let metaDist =
		percentRatioToMin(f1.meta.init_temp, f2.meta.init_temp) +
		absoluteDifference(f1.meta.init_tint, f2.meta.init_tint, 15) / 100 +
		//percentDifference(f1.meta.iso, f2.meta.iso) / 5 +
		absoluteDifference(f1.meta.flash, f2.meta.flash, 0.2) +
		cameraDifference(f1.meta.camera, f2.meta.camera) / 2 +
		formatDifference(f1.meta.format, f2.meta.format) / 2

	if (includesTimestamp) {
		metaDist += timeDifference(f1.meta.timestamp, f2.meta.timestamp) * 0.4
	}

	return metaDist
}

export function getWBDistance(f1: ImageFeature, f2: ImageFeature) {
	const wbDist =
		percentRatioToMin(f1.meta.init_temp, f2.meta.init_temp) +
		absoluteDifference(f1.meta.init_tint, f2.meta.init_tint, 15) / 100

	return wbDist
}

export function getWBDistanceFromMetadata(
	f1: PhotoMetadataRecord | undefined,
	f2: PhotoMetadataRecord | undefined,
	defaultDist: number = 0.05,
) {
	if (!f1 || !f2) {
		return defaultDist
	}
	const wbInfo1 = f1.auxiliaryData?.whiteBalanceInfo
	const wbInfo2 = f2.auxiliaryData?.whiteBalanceInfo
	if (wbInfo1 && wbInfo2) {
		const { temperature: temperature1, tint: tint1 } = wbInfo1.whiteBalance
		const { temperature: temperature2, tint: tint2 } = wbInfo2.whiteBalance
		const wbDist =
			percentRatioToMin(temperature1, temperature2) +
			absoluteDifference(tint1, tint2, 15) / 100
		return wbDist
	}
	return defaultDist
}

function getMetaDistanceNew(f1: ImageFeature, f2: ImageFeature, timeScale: number = 0.5) {
	// if one of the images has no timestamp and no camera, it is probably a jpeg reference photo; return a large default distance
	if (
		(f1.meta.timestamp == null && f1.meta.camera == null) ||
		(f2.meta.timestamp == null && f2.meta.camera == null)
	) {
		return 0.2 + 0.8 * timeScale
	}

	let metaDist =
		percentDifference(f1.meta.init_temp, f2.meta.init_temp) * 0.7 +
		(absoluteDifference(f1.meta.init_tint, f2.meta.init_tint, 15) / 80) * 0.7 +
		absoluteDifference(f1.meta.flash, f2.meta.flash, 0.2) * 0.3 +
		cameraDifference(f1.meta.camera, f2.meta.camera) * 0.1 +
		formatDifference(f1.meta.format, f2.meta.format) * 0.1

	metaDist += timeDifferenceNew(f1.meta.timestamp, f2.meta.timestamp) * timeScale

	return metaDist
}

function getSceneClusterDistance(f1: Float32Array, f2: Float32Array) {
	let d = l2Distance(f1, f2) * 2 - 0.16

	// l2 distance is originally in [0, 2], bring range of d back to [-0.16, 2.095]
	if (d > 0.35) d = (d - 0.35) * 0.5 + 0.35

	return d
}

const deepClusterDistanceCache = new Map<string, number>()

export function getDeepClusterDistance(
	imageInfo1: ImageInfoRecord,
	imageInfo2: ImageInfoRecord,
	includesImageRatio = false,
): number {
	const key = `${imageInfo1.imageId}-${imageInfo2.imageId}-${includesImageRatio}`
	if (deepClusterDistanceCache.has(key)) {
		return deepClusterDistanceCache.get(key) as number
	}
	const key2 = `${imageInfo2.imageId}-${imageInfo1.imageId}-${includesImageRatio}`
	if (deepClusterDistanceCache.has(key2)) {
		return deepClusterDistanceCache.get(key2) as number
	}
	let d = 0.5
	const f1 = imageInfo1.features
	const f2 = imageInfo2.features
	const f1SceneClusterFeature = imageInfo1.sceneClusterFeature ?? f1.scene?.clusterFeature
	const f2SceneClusterFeature = imageInfo2.sceneClusterFeature ?? f2.scene?.clusterFeature
	if (f1SceneClusterFeature && f2SceneClusterFeature) {
		d = getSceneClusterDistance(f1SceneClusterFeature, f2SceneClusterFeature)
	} else if (f1.deepCluster?.deepClusterFeature && f2.deepCluster?.deepClusterFeature) {
		d = l2Distance(f1.deepCluster.deepClusterFeature, f2.deepCluster.deepClusterFeature) - 0.2
	}

	// compensate for image ratio difference, as it often leads to larger distance for same scenes
	const result = includesImageRatio ? d - imageRatioDifference(f1, f2) : d
	deepClusterDistanceCache.set(key, result)
	return result
}

export function getDeepClusterDistanceForCulling(f1: CullingInfoRecord, f2: CullingInfoRecord) {
	const f1SceneClusterFeature = f1.sceneClusterFeature ?? f1.scene?.clusterFeature
	const f2SceneClusterFeature = f2.sceneClusterFeature ?? f2.scene?.clusterFeature
	if (f1SceneClusterFeature && f2SceneClusterFeature) {
		return getSceneClusterDistance(f1SceneClusterFeature, f2SceneClusterFeature)
	} else if (f1.deepClusterFeature && f2.deepClusterFeature) {
		return l2Distance(f1.deepClusterFeature, f2.deepClusterFeature) - 0.2
	} else {
		return 1
	}
}

function indoorOutdoorDistance(sceneFeature1: number[], sceneFeature2: number[]) {
	const indoor1 = clamp(sceneFeature1[4] ?? 0, -0.3, 0.3)
	const indoor2 = clamp(sceneFeature2[4] ?? 0, -0.3, 0.3)
	const landscape1 = clamp(sceneFeature1[5] ?? 0, -0.3, 0.3)
	const landscape2 = clamp(sceneFeature2[5] ?? 0, -0.3, 0.3)
	const d1 = Math.abs(indoor1 - indoor2)
	const d2 = Math.abs(landscape1 - landscape2)
	// console.log("indoor outdoor distance", { indoor1, indoor2, landscape1, landscape2, d1, d2 })
	return Math.min(d1 * 0.6 + d2 * 0.4, 1)
}

function getSceneDistance(f1: ImageFeature, f2: ImageFeature, defaultValue: number) {
	if (f1.scene && f2.scene) {
		const dOverall = l2Distance(f1.scene.sceneFeature, f2.scene.sceneFeature) / 2.5
		const dIndoorOutdoor = indoorOutdoorDistance(f1.scene.sceneFeature, f2.scene.sceneFeature)
		const d = dOverall * 0.6 + dIndoorOutdoor * 0.6
		// console.log("scene distance", { dOverall, dIndoorOutdoor, d })
		const isSameFaceType = (f1.wb.face && f2.wb.face) || (!f1.wb.face && !f2.wb.face)
		if (f1.scene.sceneLabel === f2.scene.sceneLabel && isSameFaceType) return d - 0.01
		else return d
	}
	return defaultValue
}

export function getOverallLightDistance(f1: ImageFeature, f2: ImageFeature) {
	return meanAbsoluteDifference(f1.light.percentiles, f2.light.percentiles)
}

// Discourage mapping face photo to non-face photo
export function getSceneThreshDelta(imageInfo: ImageInfoRecord, otherImageInfo: ImageInfoRecord) {
	let deltaT = 0
	if (imageInfo.features.light.faceLum && !otherImageInfo.features.light.faceLum) {
		deltaT = -0.02
	}
	return deltaT
}

function getColorPatchesDistance(f1: ImageFeature, f2: ImageFeature, defaultValue: number = 0.3) {
	const f1ColorPatches = f1.colorMatch?.colorPatches
	const f2ColorPatches = f2.colorMatch?.colorPatches
	if (
		f1ColorPatches &&
		f2ColorPatches &&
		f1ColorPatches.length > 0 &&
		f2ColorPatches.length > 0
	) {
		const defaultW1 = 1.0 / (f1ColorPatches?.length ?? 1)
		const defaultW2 = 1.0 / (f2ColorPatches?.length ?? 1)

		let totalD1 = 0
		for (const color1 of f1ColorPatches) {
			let minToneDiff = 1
			for (const color2 of f2ColorPatches) {
				const toneDiff = colorDifference(
					color1.slice(0, 3) as vec3,
					color2.slice(0, 3) as vec3,
				)
				if (toneDiff < 0) continue
				if (toneDiff < minToneDiff) {
					minToneDiff = toneDiff
				}
			}
			// color diff weight by regulated color patch weight
			const w1 = (color1[3] ?? defaultW1) * 0.5 + defaultW1 * 0.5
			totalD1 += minToneDiff * w1
		}

		let totalD2 = 0
		for (const color2 of f2ColorPatches) {
			let minToneDiff = 1
			for (const color1 of f1ColorPatches) {
				const toneDiff = colorDifference(
					color1.slice(0, 3) as vec3,
					color2.slice(0, 3) as vec3,
				)
				if (toneDiff < 0) continue
				if (toneDiff < minToneDiff) {
					minToneDiff = toneDiff
				}
			}
			const w2 = (color2[3] ?? defaultW2) * 0.5 + defaultW2 * 0.5
			totalD2 += minToneDiff * w2
		}

		// console.log("color patches distance", { f1ColorPatches, f2ColorPatches, totalD1, totalD2 })
		//return (totalD1 + totalD2) / 2 + Math.max(totalD1, totalD2) * 0.5
		return totalD1 * 0.9 + totalD2 * 0.6 // f2 is ref, we weight more on map f1 to ref
	}

	return defaultValue
}

function overallLightingDifference(f1: ImageFeature, f2: ImageFeature) {
	const f1Lighting = f1.light.percentiles
	const f2Lighting = f2.light.percentiles
	const minf1 = f1Lighting[0] ?? 0
	const maxf1 = f1Lighting[f1Lighting.length - 1] ?? 1
	const minf2 = f2Lighting[0] ?? 0
	const maxf2 = f2Lighting[f2Lighting.length - 1] ?? 1
	const diff = meanAbsoluteDifference(f1Lighting, f2Lighting)

	// scale the percentiles to min, max equal to [0, 1] as a normalization
	const f1LightingScaled = f1Lighting.map((p) => (p - minf1) / Math.max(maxf1 - minf1, 0.01))
	const f2LightingScaled = f2Lighting.map((p) => (p - minf2) / Math.max(maxf2 - minf2, 0.01))
	const diffNormalized = meanAbsoluteDifference(f1LightingScaled, f2LightingScaled)

	return diff * 0.3 + diffNormalized * 0.7
}

function faceLightingDifference(f1: ImageFeature, f2: ImageFeature, defaultValue: number = 0.4) {
	const f1FaceLighting = f1.light.facePercentiles
	const f2FaceLighting = f2.light.facePercentiles

	if (f1FaceLighting.length !== f2FaceLighting.length) {
		return defaultValue
	}
	if (f1FaceLighting.length === 0) {
		return 0.02 // default distance for no face photo
	}
	const diff = meanAbsoluteDifference(f1FaceLighting, f2FaceLighting)

	// increase f1's 50% percentile to be equal to f2's 50% percentile as a normalization
	const delta = (f2FaceLighting[2] ?? 0) - (f1FaceLighting[2] ?? 0)
	const f1FaceLightingScaled = f1FaceLighting.map((p) => p + delta)
	const diffNormalized = meanAbsoluteDifference(f1FaceLightingScaled, f2FaceLighting)

	return diff * 0.4 + diffNormalized * 0.6
}

function faceWbDistance(
	f1: ImageFeature,
	f2: ImageFeature,
	defaultValue: number = 0.4,
	scale: number = 1,
) {
	const f1FaceWb = f1.wb.face
	const f2FaceWb = f2.wb.face
	if (!f1FaceWb && !f2FaceWb) return defaultValue / 10
	if (!f1FaceWb || !f2FaceWb) return defaultValue

	const diff = l2Distance(f1FaceWb, f2FaceWb)

	const lum1 = (f1FaceWb[0] + f1FaceWb[1] + f1FaceWb[2]) / 3
	const lum2 = (f2FaceWb[0] + f2FaceWb[1] + f2FaceWb[2]) / 3
	const normalized1 = f1FaceWb.map((p) => p / lum1)
	const normalized2 = f2FaceWb.map((p) => p / lum2)
	const diffNormalized = meanAbsoluteDifference(normalized1, normalized2)

	return (diff * 0.4 + diffNormalized * 0.6) * scale
}

export function imageWbDistance(f1: ImageFeature, f2: ImageFeature, scale: number = 5) {
	const f1ImageWb = f1.wb.image
	const f2ImageWb = f2.wb.image
	const dist = l2Distance(f1ImageWb, f2ImageWb) * scale
	return getDecreasedValue(dist, 0.15, 0.3)
}

export function getFeatureDistances(
	imageInfo1: ImageInfoRecord,
	imageInfo2: ImageInfoRecord,
	includesTimestamp = true,
	onlyF1IsInput = true, // true: f1 is input, f2 is reference
): FeatureDistances {
	const f1 = imageInfo1.features
	const f2 = imageInfo2.features
	const sceneDist1 = getDeepClusterDistance(imageInfo1, imageInfo2)
	const sceneDist2 = getSceneDistance(f1, f2, sceneDist1)
	const scene1Weight = clamp(1.4 - (sceneDist1 - 0.22) * 5, 1.1, 1.4) // 0.22 - 1.4, 0.28 - 1.1
	const scene2Weight = 1.7 - scene1Weight
	const sceneOverallScale = 0.35 / 0.55
	let sceneDist = (sceneDist1 * scene1Weight + sceneDist2 * scene2Weight) * sceneOverallScale
	sceneDist = getDecreasedValue(sceneDist, 0.3, 1.2) // actually increase the sceneD if it's larger than 0.3
	// console.log("sceneDist between", imageInfo1.imageId, imageInfo2.imageId, { sceneDist1, sceneDist2, sceneDist })

	const inputHasFace = f1.light.facePercentiles.length > 0
	const refHasFace = f2.light.facePercentiles.length > 0
	let faceDiffDist = 0.35 // old: 0.5

	const faceAveArea = f1.light.facePercent / Math.max(f1.light.faceCount, 1)
	const refFaceAveArea = f2.light.facePercent / Math.max(f2.light.faceCount, 1)
	const faceAreaDiff =
		Math.abs(faceAveArea - refFaceAveArea) / Math.max(faceAveArea + refFaceAveArea, 0.0001)

	// encourage to map non-face photo to face photo, but not vice versa
	if (onlyF1IsInput && !inputHasFace && refHasFace) faceDiffDist = 0.12

	let lightDist =
		overallLightingDifference(f1, f2) + faceLightingDifference(f1, f2, faceDiffDist * 0.8) * 0.6
	lightDist = getDecreasedValue(lightDist, 0.2, 0.5)

	const overallWbDist = imageWbDistance(f1, f2)
	const faceScale = (f1.wb.skinWeight + f2.wb.skinWeight) / 2
	const faceWbDist = faceWbDistance(f1, f2, faceDiffDist, faceScale)
	const wbDist = overallWbDist + faceWbDist * 0.6

	const colorDist = getColorPatchesDistance(f1, f2, wbDist)

	let wbColorDist = wbDist * 0.75 + colorDist * 0.75 + clamp(faceAreaDiff * 0.04, 0, 0.04)
	wbColorDist = getDecreasedValue(wbColorDist, 0.25, 0.6)
	// console.log("wbColorDist", { wbDist, overallWbDist, faceWbDist, colorDist, faceAreaDiff, wbColorDist })

	const useNewMeta = imageInfo2.colorMatchReference != null
	const metaDist = useNewMeta
		? getMetaDistanceNew(f1, f2, includesTimestamp ? 0.5 : 0)
		: getMetaDistance(f1, f2, includesTimestamp)

	const distances = [
		sceneDist,
		lightDist,
		wbColorDist,
		Math.min(metaDist, sceneDist * 3),
	] as const
	const overallDist = getWeightedSumDistance(distances, overallDistanceWeight)

	return [sceneDist, lightDist, wbColorDist, metaDist, overallDist, sceneDist1, overallWbDist]
}

export function getDistanceMap(
	imageInfo: ImageInfoRecord,
	referenceImages: Record<string, ImageInfoRecord>,
	excludeBWRef: boolean = true,
): DistanceMap {
	// const endMeasurement = startMeasurement("getDistanceMap")
	const distance = {}
	for (const imageId in referenceImages) {
		const referenceImage = referenceImages[imageId]
		if (referenceImage) {
			if (excludeBWRef && referenceImage.biases?.saturation === saturationBiasBW) continue
			distance[imageId] = getFeatureDistances(imageInfo, referenceImage)
		}
	}
	// endMeasurement()
	// logAiInfo("getDistanceMap ref number:", Object.keys(referenceImages).length)
	return distance
}

export function getWeightedSum(distances: readonly number[], weights: number[]) {
	let sum = 0
	const n = Math.min(distances.length, weights.length)
	for (let i = 0; i < n; i++) {
		sum += <number>distances[i] * <number>weights[i]
	}
	return sum
}

function getWeightedSumDistance(
	distances: readonly number[],
	weights: number[],
	minResult = 0.0000001,
) {
	const sum = getWeightedSum(distances, weights)
	return Math.max(sum, minResult)
}

export function getInverseDistanceWeights(distances: number[], offsetScale = 0.75) {
	// add a default dist, so that if nearestRef's distance is too large, it will be counted less
	// distances.push(defaultDist) //defaultDist = 0.4,

	const offset = Math.min(...distances) * offsetScale // make weights larger for closest neighbor
	let weights = distances.map((a) => 1.0 / (a - offset))
	weights = normalizeBySum(weights)

	return weights
}

export function getWeightsFromRefs(kNeighbors: [string, number[]][]) {
	// coeffs for [sceneDist, lightDist, wbDist, metaDist, overallDist]
	const lightDistWeights = [0.3, 0.35, 0.1, 0.1, 0.15] //[0.4, 0.4, 0.1, 0.1]
	const wbDistWeights = [0.3, 0.1, 0.3, 0.15, 0.15] //[0.45, 0.1, 0.3, 0.15]
	const lightDistances: number[] = []
	const colorDistances: number[] = []
	const overallDistances: number[] = []
	for (const item of kNeighbors) {
		const lightDist = getWeightedSumDistance(item[1], lightDistWeights)
		lightDistances.push(lightDist)

		const wbDist = getWeightedSumDistance(item[1], wbDistWeights)
		colorDistances.push(wbDist)

		overallDistances.push(item[1][4] ?? 0)
	}
	logAiInfo("distances", { lightDistances, colorDistances, overallDistances })
	const lightWeights = getInverseDistanceWeights(lightDistances)
	const colorWeights = getInverseDistanceWeights(colorDistances)
	const overallWeights = getInverseDistanceWeights(overallDistances)

	return { lightWeights, colorWeights, overallWeights }
}

export function getValidNearestRefId(kNeighbors: [string, number[]][] | null) {
	if (!kNeighbors) return null
	let nearestID = kNeighbors[0]?.[0] ?? ""
	if (nearestID === defaultReferenceId) {
		nearestID = kNeighbors[1]?.[0] ?? nearestID
	}
	return nearestID
}

export function getValidNearestRefOverallDist(kNeighbors: [string, number[]][] | null) {
	if (!kNeighbors) return defaultOverallDist
	const nearestID = kNeighbors[0]?.[0] ?? ""
	let nearestOverallDist = kNeighbors[0]?.[1][4] ?? defaultOverallDist
	if (nearestID === defaultReferenceId) {
		nearestOverallDist = kNeighbors[1]?.[1][4] ?? nearestOverallDist
	}
	return nearestOverallDist
}

export function getValidNearestRef(
	kNeighbors: [string, number[]][] | null,
	referencePhotos: Record<string, ImageInfoRecord>,
) {
	const nearestID = getValidNearestRefId(kNeighbors)
	return nearestID ? referencePhotos[nearestID] : null
}

export function getTimeWeightDecreasedOverallDistToRef(
	imageInfo: ImageInfoRecord,
	refInfo: ImageInfoRecord | undefined | null,
) {
	const kNeighbors = imageInfo.nearestReferences
	if (!kNeighbors) return defaultOverallDist
	const nearestID = kNeighbors[0]?.[0] ?? ""
	let allDists = kNeighbors[0]?.[1]
	if (refInfo) {
		allDists = kNeighbors.find((item) => item[0] === refInfo.imageId)?.[1]
	} else if (nearestID === defaultReferenceId) {
		allDists = kNeighbors[1]?.[1]
	}

	if (!allDists || allDists.length < 5 || !refInfo) return allDists?.[4] ?? defaultOverallDist

	const [sceneDist, lightDist, wbDist] = allDists.slice(0, 3) as [number, number, number]
	const newMetaDist = getMetaDistanceNew(imageInfo.features, refInfo.features, 0.2)

	const overallDist = getWeightedSumDistance(
		[sceneDist, lightDist, wbDist, newMetaDist],
		overallDistanceWeight,
	)

	return overallDist
}

function shouldUseFullColorMatch(
	imageInfo: ImageInfoRecord,
	nearestRef: ImageInfoRecord | undefined,
	nearestDists: number[] | undefined,
	method: "colorMatch" | "preset" | "mixed" = "preset",
) {
	if (method === "preset") return false
	if (method === "colorMatch") return true

	if (!nearestRef?.colorMatchReference || !nearestDists) return false

	const nearestSceneDist = nearestDists[0] ?? 1
	const clusterDist = nearestDists[5] ?? getDeepClusterDistance(imageInfo, nearestRef)
	const overallDist = nearestDists[4] ?? 1

	const timeDiffSeconds =
		((imageInfo.features.meta.timestamp ?? 0) - (nearestRef.features.meta.timestamp ?? 0)) /
		1000

	if (overallDist > 0.32) return true

	const minSceneDist = Math.min(nearestSceneDist, clusterDist)
	// for photos near in time, do not use color match unless the distance is very large
	if (
		(timeDiffSeconds <= 180 && (minSceneDist <= 0.3 || minSceneDist + overallDist < 0.52)) ||
		(timeDiffSeconds <= 30 && (minSceneDist <= 0.32 || minSceneDist + overallDist < 0.53))
	)
		return false

	// for photos far in time, use color match unless the scene distance is very small
	if (timeDiffSeconds / 60 > 40 && clusterDist > 0.22 && overallDist > 0.22) return true

	return (
		(overallDist > 0.27 && nearestSceneDist > 0.24) ||
		nearestSceneDist > 0.27 ||
		clusterDist > 0.28
	)
}

/**
 * Compute the preset ID and blended bias term for applying a style to a photo.
 *
 * **Old algorithm** (in-app edited references with local adjustments or a user LUT):
 * The bias is a weighted average of each reference's stored `biases` — the per-key delta
 * between the user's edits and the auto-adjustments (`user_edit[key] − auto[key]`).
 * Separate weight sets are used for light keys, WB keys, and color keys to reflect the
 * different feature distances that govern each domain. The result is added on top of the
 * photo's own auto adjustments to reproduce the reference's creative intent.
 *
 * **New algorithm** (via neural color match):
 * This function is still called, but only `presetId` is used. The `biases` returned will
 * be near-empty or all 0 because `approveJpegReference` stores an near-empty `biases` in reference imageInfo.
 *
 * Returns `{ presetId: null, bias: {} }` if no preset is found on the nearest reference to
 * use full color match path.
 *
 * @param imageInfo - The photo being styled
 * @param kNeighbors - K nearest references as `[referenceId, featureDistances]` pairs
 * @param referencePhotos - Map of all reference ImageInfoRecords, keyed by photo ID
 * @returns `presetId` to apply and a `bias` map of weighted adjustment deltas
 */
export function getPresetAndBias(
	imageInfo: ImageInfoRecord,
	kNeighbors: [string, number[]][],
	referencePhotos: Record<string, ImageInfoRecord>,
) {
	let nearestID = kNeighbors[0]?.[0] ?? ""
	let nearestDists = kNeighbors[0]?.[1]
	let nearestOverallDist = kNeighbors[0]?.[1][4] ?? 1
	if (nearestID === defaultReferenceId) {
		nearestID = kNeighbors[1]?.[0] ?? nearestID
		nearestDists = kNeighbors[1]?.[1]
		nearestOverallDist = kNeighbors[1]?.[1][4] ?? 1
	}
	const nearestRef = referencePhotos[nearestID]
	const preset = nearestRef?.presetId ?? null

	logAiInfo("getPresetAndBias from references:", {
		kNeighbors,
		nearestRefs: kNeighbors.map((item) => referencePhotos[item[0]]),
		preset,
	})

	// If no preset, return and use full color match path
	// shouldUseFullColorMatch is always false; it was a debug feature for internal algorithm testing
	if (!preset || shouldUseFullColorMatch(imageInfo, nearestRef, nearestDists))
		return { presetId: null, bias: {} }

	const { lightWeights, colorWeights, overallWeights } = getWeightsFromRefs(kNeighbors)

	const nearestRefPhotos = kNeighbors.map((item) => referencePhotos[item[0]])

	const bias = {}
	const defaultAdjustments = getDefaultAdjustments()
	for (const key of allAdjustmentKeys) {
		if (DefaultExcludedAdjustmentKeys.includes(key)) continue
		if (key === "colorProfile") {
			const refProfile = nearestRef?.biases?.[key]
			if (refProfile !== undefined) {
				bias[key] = refProfile
				logAiInfo("colorProfile bias:", key, refProfile)
			}
			continue
		}
		const isWbKey = wbKeys.some((wbKey) => wbKey === key)
		if (!isWbKey && typeof defaultAdjustments[key] !== "number") {
			if ((key.includes("curves") || key === "pointColors") && nearestOverallDist < 0.4) {
				const refCurves = nearestRef?.biases?.[key]
				if (refCurves) {
					bias[key] = refCurves
					logAiInfo("curve/pointColors bias:", key, refCurves)
				}
			} else if (key === "localAdjustments") {
				// modified preset's localAdjustments
				const presetLocalBias = nearestRef?.biases?.[key]
				if (presetLocalBias) {
					const refLocalBiases = nearestRefPhotos.map((photo) =>
						photo?.biases ? photo.biases.localAdjustments || [] : [],
					)
					bias[key] = computeWeightedLocalBiases(
						presetLocalBias,
						refLocalBiases,
						lightWeights,
					)
					logAiInfo("localAdjustments bias:", key, presetLocalBias)
				}
			}
			continue
		}

		const refBiases = nearestRefPhotos.map((photo) =>
			photo?.biases ? photo.biases[key] || 0 : 0,
		) as number[]
		// refBiases.push(0) // add default bias 0

		const isLightKey = lightKeys.some((lightKey) => lightKey === key)
		if (isLightKey) {
			bias[key] = getWeightedSum(refBiases, lightWeights)
		} else if (isWbKey) {
			bias[key] = getWeightedSum(refBiases, colorWeights)
		} else {
			bias[key] = getWeightedSum(refBiases, overallWeights)
		}
		if (bias[key] != 0) {
			logAiInfo("nonzero bias:", key, bias[key], {
				refBiases,
				lightWeights,
				colorWeights,
				overallWeights,
			})
		}
	}

	return { presetId: preset, bias: bias }
}

/**
 * Select the K nearest reference photos by overall feature distance.
 *
 * After ranking, outlier references whose distance is too far from the closest
 * reference are pruned (via `removeDifferentRef`), so the returned list may have
 * fewer than K entries. This keeps color blending focused on genuinely similar
 * references rather than averaging in a very different-looking shot.
 *
 * @param distanceMap - Per-reference feature distances (scene, light, WB, meta, overall, cluster)
 * @param excludeDifferentRef - When true (default), drop references that are outliers relative to the nearest one
 * @returns Up to K `[referenceId, distances]` pairs, sorted nearest-first
 */
export function getKNN(
	distanceMap: DistanceMap,
	excludeDifferentRef: boolean = true,
): [string, FeatureDistances][] {
	// add a default zero-bias reference
	distanceMap[defaultReferenceId] = defaultDistances

	const sortedArray = Object.entries(distanceMap).sort((a, b) => {
		return a[1][4] - b[1][4]
	})
	let knn = sortedArray.slice(0, getDefaultKNNRefs())

	knn = excludeDifferentRef ? removeDifferentRef(knn) : knn

	return knn
}

function removeDifferentRef(knn: [string, FeatureDistances][]) {
	// Exclude references that are too different compared to the nearest reference
	if (knn.length > 1) {
		const minDist =
			knn[0]?.[0] != defaultReferenceId ? (knn[0]?.[1][4] ?? 1) : (knn[1]?.[1][4] ?? 1)
		knn = knn.filter((item) => item[1][4] < minDist + 0.06)
	}

	// Filter out references that are too different from the two nearest references in terms of image wb distance
	// because this usually contributes to overall temperature and tint of the photo
	if (knn.length >= 3) {
		knn = remove3rdDifferentWbRef(knn)
	}
	return knn
}

function remove3rdDifferentWbRef(knn: [string, FeatureDistances][]) {
	if (knn.length >= 4) {
		knn = knn.filter((item) => item[0] != defaultReferenceId)
	}
	if (knn.length < 3) return knn
	const ref1WbDist = knn[0]?.[1][6] ?? 0
	const ref2WbDist = knn[1]?.[1][6] ?? 0
	const ref3WbDist = knn[2]?.[1][6] ?? 0
	// only remove the 3rd reference if it's too different from both ref1 and ref2 in terms of image wb distance.
	if (
		ref3WbDist > ref1WbDist + Math.max(0.04, ref1WbDist * 0.6) &&
		ref3WbDist > ref2WbDist + Math.max(0.04, ref2WbDist * 0.6)
	) {
		knn = knn.slice(0, 2)
		logAiInfo("!!Removed 3rd reference", { ref1WbDist, ref2WbDist, ref3WbDist })
	}
	return knn
}

export function getUpdatedKNN(
	distanceMap: DistanceMap,
	oldNearestReferences: [string, FeatureDistances][],
) {
	if (isEmpty(distanceMap)) return { kNeighborsNew: oldNearestReferences, updated: false }

	const K = getDefaultKNNRefs()
	let sortedNewRefs = Object.entries(distanceMap).sort((a, b) => a[1][4] - b[1][4])
	sortedNewRefs = sortedNewRefs.slice(0, K)

	const oldReferenceIDs = oldNearestReferences.map((item) => item[0])
	const oldMinDistance = oldNearestReferences[0]?.[1][4] ?? 0
	let oldMaxDistance = oldNearestReferences[oldNearestReferences.length - 1]?.[1][4] ?? 0
	if (oldNearestReferences.length == 1 && K > 1) {
		oldMaxDistance = oldMinDistance + 0.1
	}

	// only replace existing ref if distance < threshold to reduce the chance of meaningless reprocessing
	const distanceThresh = Math.min(oldMaxDistance * 0.5 + oldMinDistance * 0.5, defaultOverallDist)

	let updated = false
	for (const item of sortedNewRefs) {
		if (oldReferenceIDs.includes(item[0])) {
			updated = true
		} else if (item[1][4] < distanceThresh) {
			updated = true
			oldNearestReferences.push(item)
		}
	}

	if (updated) {
		oldNearestReferences = oldNearestReferences.sort(([, a], [, b]) => {
			return a[4] - b[4]
		})
		const kNeighborsNew = removeDifferentRef(oldNearestReferences.slice(0, K))
		return {
			kNeighborsNew,
			updated: updated,
		}
	} else {
		return { kNeighborsNew: oldNearestReferences, updated: updated }
	}
}

export function isEqual(
	bias1: Partial<BiasType>,
	bias2: Partial<BiasType>,
	ignoreBiasKeys: string[] | null = null,
	temperatureThresh: number = 0.015,
	eps: number = 0.03,
) {
	let diffSum = 0
	let count = 0
	for (const key of allBiasKeys) {
		if (ignoreBiasKeys && ignoreBiasKeys.includes(key)) continue

		const bias1Value = bias1[key]
		const bias2Value = bias2[key]
		if (bias1Value == undefined && bias2Value == undefined) {
			continue
		}
		if (
			(bias1Value != undefined && bias2Value == undefined) ||
			(bias1Value == undefined && bias2Value != undefined)
		) {
			return false
		}
		if (typeof bias1Value !== typeof bias2Value) {
			return false
		}
		if (typeof bias1Value !== "number" || typeof bias2Value !== "number") {
			// curve, crop, localAdjustments, pointColors, healing
			if (key === "crop") {
				if (!isCropEqual(bias1Value as Crop, bias2Value as Crop)) {
					return false
				}
			} else if (!equal(bias1Value, bias2Value)) {
				return false
			}
			continue
		}
		let threshold = eps
		switch (key) {
			case "temperature":
				threshold = temperatureThresh
				break
			case "tint":
				threshold = 1.5
				break
			case "exposure":
				threshold = 0.05
				break
		}
		const diff = Math.abs(bias1Value - bias2Value)
		if (diff > threshold) {
			return false
		}
		if (diff > 0) {
			diffSum += diff / threshold
			count++
		}
	}
	const diffMean = diffSum / Math.max(count, 1)
	if (diffMean > 0.6) return false

	return true
}

export function isEqualForKeys(
	bias1: Partial<Adjustments>,
	bias2: Partial<Adjustments>,
	keys: AdjustmentKey[],
) {
	const adjustment1 = extractKeys(bias1, keys)
	const adjustment2 = extractKeys(bias2, keys)

	return isEqual(adjustment1, adjustment2)
}

export function getTemperatureBias(userTempAdjustment: number, aiTempAdjustment: number) {
	return (userTempAdjustment - aiTempAdjustment) / aiTempAdjustment
}

export function applyTemperatureBias(aiTempAdjustment: number, bias: number) {
	const finalTemperature = aiTempAdjustment * (1 + bias)

	return clamp(finalTemperature, ...getAdjustmentRange("temperature"))
}
